/**
 * Creem服务端支付服务
 * 仅在服务端使用，包含API密钥，不暴露给前端
 * 注意：此文件仅用于开发环境测试，生产环境应使用Netlify函数
 */

// import i18n from '@/i18n'; // 改为动态导入避免TDZ
import { Creem } from 'creem';
import QRCode from 'qrcode';
import request from './request';

// 开发环境直接使用Creem SDK
const creem = new Creem();

/**
 * 创建支付检查点
 * @param priceId 价格ID
 * @param customerEmail 客户邮箱（可选）
 * @returns 支付检查点信息
 */
export async function createCreemCheckout(priceId: string, customerEmail?: string) {
  try {
    // 检查环境变量
    const apiKey = import.meta.env.VITE_CREEM_API_KEY;
    if (!apiKey) {
      throw new Error('Creem APIkeynotconfiguration');
    }

    console.log('creatingCreem支付checking点:', { priceId, customerEmail });

    // 调用Creem API - 正确的参数结构
    const checkout = await creem.createCheckout({
      productId: priceId,
      xApiKey: apiKey,
    });

    console.log('Creem APIresponse:', checkout);

    return {
      success: true,
      checkout: checkout,
      url: checkout.alipayQrCodeUrl || checkout.alipay_qr_code_url || (checkout.qrCodes && checkout.qrCodes.alipay) || null
    };
  } catch (error: any) {
    console.error('Creem API调用failed:', error);
    throw new Error(error.message || 'u64cdu4f5cu5931u8d25');
  }
}

/**
 * 获取支付宝二维码URL
 * @param priceId 价格ID
 * @param customerEmail 客户邮箱（可选）
 * @returns 支付宝二维码URL
 */
export async function getAlipayQRCode(priceId: string, customerEmail?: string) {
  try {
    const result = await createCreemCheckout(priceId, customerEmail);

    // 提取支付宝二维码URL
    const alipayQr =
      result.checkout.alipayQrCodeUrl ||
      result.checkout.alipay_qr_code_url ||
      (result.checkout.qrCodes && result.checkout.qrCodes.alipay) ||
      result.url ||
      null;

    if (!alipayQr) {
      throw new Error('u64cdu4f5cu5931u8d25');
    }

    return {
      success: true,
      qrUrl: alipayQr,
      price: result.checkout.amount ? (typeof result.checkout.amount === 'number' ? result.checkout.amount / 100 : parseFloat(result.checkout.amount) / 100) : null
    };
  } catch (error: any) {
    console.error('getting支付宝二维码failed:', error);
    throw error;
  }
}

/**
 * 生成支付宝二维码图片
 * @param priceId 价格ID
 * @param customerEmail 客户邮箱（可选）
 * @returns 二维码图片的DataURL
 */
export async function generateAlipayQRCode(priceId: string, customerEmail?: string) {
  try {
    const result = await getAlipayQRCode(priceId, customerEmail);

    if (!result.success || !result.qrUrl) {
      throw new Error('u64cdu4f5cu5931u8d25');
    }

    // 生成二维码图片
    const qrCodeDataURL = await QRCode.toDataURL(result.qrUrl, {
      width: 200,
      margin: 2,
      color: {
        dark: 'blacklisted',
        light: 'hsl(var(--background))'
      }
    });

    return {
      success: true,
      qrCodeDataURL,
      price: result.price,
      originalUrl: result.qrUrl
    };
  } catch (error: any) {
    console.error('生成二维码failed:', error);
    throw error;
  }
}

/**
 * 创建支付检查点并跳转到Creem支付页面
 * @param priceId 价格ID
 * @param customerEmail 客户邮箱（可选）
 * @returns 支付页面URL
 */
export async function startCheckout(priceId: string, customerEmail?: string) {
  try {
    console.log('starts支付stream程:', { priceId, customerEmail });

    // 调用API创建支付检查点（统一 request）
    const data = await request.post('/.netlify/functions/api/creem/checkout', {
      priceId,
      customerEmail
    });
    console.log('支付checking点creatingsuccess:', data);

    if (!data.success || !data.url) {
      throw new Error('无法获取支付页面URL');
    }

    return {
      success: true,
      url: data.url,
      checkout: data.checkout
    };
  } catch (error: any) {
    console.error('creating支付checking点failed:', error);
    throw error;
  }
}

/**
 * 跳转到Creem支付页面
 * @param priceId 价格ID
 * @param customerEmail 客户邮箱（可选）
 */
export async function redirectToCheckout(priceId: string, customerEmail?: string) {
  try {
    const result = await startCheckout(priceId, customerEmail);

    if (result.success && result.url) {
      // 跳转到Creem支付页面
      window.location.href = result.url;
    } else {
      throw new Error('无法获取支付页面URL');
    }
  } catch (error: any) {
    console.error('跳转到支付pagefailed:', error);
    throw error;
  }
}
